import { doc, collection, getDoc, getDocs, newId } from '@battleops/localstore';
import { completeTask, stopTask, completeSectionWrites, setProjectStatus, isDepartmentMode } from '@battleops/shared';
import { db } from './store';
import { commitWrites } from './commit';

// Multi-doc operations that must land together. Each is one local batch = one outbox group, so the
// server applies it in a single transaction too. The WRITE LOGIC lives in shared/commands (the MCP
// runs the same code); these wrappers only gather the current docs and commit the result.

const all = async (name) => (await getDocs(collection(db, name))).docs.map((d) => ({ id: d.id, ...d.data() }));
const one = async (path) => { const s = await getDoc(doc(db, ...path.split('/'))); return s.exists() ? s.data() : null; };

async function ctxFor({ tasks = null, sections = true, sessions = true } = {}) {
  const [t, s, ss, lock, timer, clock] = await Promise.all([
    tasks ?? all('tasks'), sections ? all('sections') : [], sessions ? all('clockSessions') : [], one('meta/lock'), one('meta/timer'), one('meta/clock'),
  ]);
  return { tasks: t, sections: s, sessions: ss, lock, timer, clock, now: Date.now() };
}

export async function atomicCompleteTask({ taskId, elapsed = 0, pauseTime = 0, sessionId, timeEntry, allTasks = null }) {
  if (!taskId) throw new Error('taskId required');
  const ctx = await ctxFor({ tasks: allTasks });
  if (!ctx.tasks.some((t) => t.id === taskId)) throw new Error('Task not found');
  const { writes, unlockedSectionId } = completeTask(ctx, { id: taskId, elapsed, pauseTime, sessionId, timeEntry });
  await commitWrites(writes);
  return { success: true, taskId, unlockedSectionId };
}

// `keepTimer` / `keepLock`: bank time (or write skip metadata) without touching a timer or lock
// that belongs to a different task.
export async function atomicStopTimer({ taskId, elapsed = 0, pauseTime = 0, sessionId, timeEntry, skippedCount, lastSkippedAt, keepTimer = false, keepLock = false }) {
  if (!taskId) throw new Error('taskId required');
  const ctx = await ctxFor({ sections: false });
  const { writes } = stopTask(ctx, { id: taskId, elapsed, pauseTime, sessionId, timeEntry, skip: typeof skippedCount === 'number', keepTimer, keepLock });
  if (typeof skippedCount === 'number') {
    const w = writes.find((x) => x.path === `tasks/${taskId}`);
    if (w) Object.assign(w.data, { skippedCount, lastSkippedAt: lastSkippedAt || Date.now() });
  }
  await commitWrites(writes);
  return { success: true };
}

// Section fully done → mark it completed, unlock the next locked section and its tasks.
export async function atomicCompleteSection({ sectionId, projectId }) {
  const ctx = await ctxFor({ sessions: false });
  const { writes, unlockedSectionId } = completeSectionWrites(ctx, { sectionId, projectId });
  await commitWrites(writes);
  return { success: true, unlockedSectionId };
}

// A project's status change (shared setProjectStatus): completed voids its unfinished tasks in the same group — the
// running one stopped and its time banked first; leaving completed with `restore` brings them back (projectclose.js).
// Reads the whole store (void tasks included, timer, lock, clock) so every caller — the status sheet, the web menus,
// the Project settings card — gets the same close. → { voided, running, banked, restored }
async function projectCtx() {
  const [ctx, projects, departments, settings] = await Promise.all([ctxFor(), all('projects'), all('departments'), one('meta/settings')]);
  return { ...ctx, clock: ctx.clock?.active ? ctx.clock : null, projects, departments, departmentMode: isDepartmentMode(settings), newId };
}
export async function atomicSetProjectStatus({ id, status, restore = false }) {
  const r = setProjectStatus(await projectCtx(), { id, status, restore });
  await commitWrites(r.writes);
  return { voided: r.voided, running: r.running, banked: r.banked, restored: r.restored };
}
